import React, { useEffect, useState, useRef } from "react";
import Chart from "chart.js/auto";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  styled,
  Fade,
  Slide,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { CofiBedrockAgentAws } from "../../util/API/cofi";
import CofiFileUploads from "../../components/CofiFileUploads/CofiFileUploads";
import PrintIcon from "@mui/icons-material/Print";
import IconButton from "@mui/material/IconButton";

const STATIC_QA = [
  {
    question:
      "What financial products or services would you find most beneficial for individuals earning over $40K annually, taking into account community and cultural priorities?",
    answer: "",
  },
  {
    question:
      "What financial tools or advice would help Indigenous homeowners enhance the value of their properties while preserving traditional land ties and cultural values?",
    answer: "",
  },
  {
    question:
      "What financial services can support community members facing housing instability in securing permanent housing and improving financial health?",
    answer: "",
  },
  {
    question:
      "Which financial products can help Indigenous retirees maintain a sustainable lifestyle while supporting intergenerational wealth and cultural continuity?",
    answer: "",
  },
  {
    question:
      "What financial planning tools would you recommend for young Indigenous families to save for their children's education and cultural activities while preparing for retirement?",
    answer: "",
  },
  {
    question:
      "Are there financial products or services designed to address the specific needs of Indigenous men and women, including support for traditional roles and modern aspirations?",
    answer: "No data is stored.",
  },
];

const ChatBubble = styled(Box)(({ theme, sender, thinking }) => ({
  maxWidth: "60%",
  padding: thinking ? "4px 0" : "10px 14px",
  borderRadius: "16px",
  marginBottom: "8px",
  alignSelf: sender === "user" ? "flex-end" : "flex-start",
  backgroundColor: thinking
    ? "transparent"
    : sender === "user"
    ? theme.palette.primary.main
    : theme.palette.grey[200],
  color: sender === "user" ? "#fff" : "#000",
}));

// Wizard Steps Configuration
const WIZARD_STEPS = [
  "Set up CoFi Profile",
  "Load Datasets",
  "Set Preferences",
  "Run CoFi",
  "Review Results",
  "Deploy & Launch",
];

function CoFi() {
  const [chatHistory, setChatHistory] = useState([]);
  const chatContainerRef = React.useRef(null);
  const hasStartedChat = chatHistory.length > 0;
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState("Thinking");
  const [activeTab, setActiveTab] = useState(1);

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    profile: {
      organizationName: "",
      industry: "",
      targetDemographic: "",
      sampleSize: "",
    },
    datasets: {
      uploadedFiles: [],
    },
    preferences: {
      llmModel: "Amazon Bedrock",
      cofiPartner: "Banking",
      riskTolerance: "Medium",
    },
    results: [],
  });

  const THINKING_MESSAGES = [
    "Thinking",
    "Analyzing your question",
    "Reviewing insights",
    "Connecting the dots",
    "Almost there",
  ];

  useEffect(() => {
    if (!isThinking) return;

    let index = 0;

    const interval = setInterval(() => {
      index = (index + 1) % THINKING_MESSAGES.length;
      setThinkingText(THINKING_MESSAGES[index]);
    }, 5000);

    return () => clearInterval(interval);
  }, [isThinking]);

  useEffect(() => {
    if (!selectedQuestion) return;

    const fetchAnswer = async () => {
      const response = await getAnswer(selectedQuestion);
      const answer = decodeHtml(response);

      setIsThinking(false);
      setIsTyping(true);

      let index = 0;

      const interval = setInterval(() => {
        index++;

        setChatHistory((prev) => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;

          updated[lastIndex] = {
            ...updated[lastIndex],
            text: answer.slice(0, index),
          };

          return updated;
        });

        scrollToBottom();

        if (index >= answer.length) {
          clearInterval(interval);
          setIsTyping(false);
        }
      }, 20);
    };

    fetchAnswer();
  }, [selectedQuestion]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const getAnswer = async (question) => {
    try {
      const response = await CofiBedrockAgentAws(
        "abc",
        "1",
        "ANALYST",
        question,
        "RETAIL",
        "NA",
        0.85
      );
      console.log("getAnswer==============", response);

      if (response?.data?.header?.callStatus == "1") {
        return response.data.header.agentResponse;
      }

      return "Sorry, I couldn't fetch an answer right now.";
    } catch (err) {
      console.error(err);
      return "Something went wrong. Please try again.";
    }
  };

  const decodeHtml = (text) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const handleSelect = (_, value) => {
    if (!value) return;

    if (!hasWelcomed) {
      setHasWelcomed(true);
    }

    setSelectedQuestion(value.question);

    setIsThinking(true);
    setIsTyping(true);

    setChatHistory((prev) => [
      ...prev,
      { sender: "user", text: value.question },
      { sender: "bot", text: "" },
    ]);

    setTimeout(scrollToBottom, 0);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const TypingIndicator = ({ text }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        color: "#555",
        fontStyle: "italic",
      }}
    >
      {text && <span>{text}</span>}
      <Box sx={{ display: "flex", gap: "4px" }}>
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </Box>
    </Box>
  );

  const handlePrintChat = () => {
    const printWindow = window.open("", "_blank");

    const html = `
      <html>
        <head>
          <title>CoFi Chat</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h2 { margin-bottom: 20px; }
            .msg { margin-bottom: 12px; }
            .user { font-weight: bold; }
            .bot { font-weight: bold; }
            .bubble { padding: 8px 12px; border-radius: 8px; background: #f3f3f3; display: inline-block; max-width: 90%; }
          </style>
        </head>
        <body>
          <h2>CoFi Conversation</h2>
          ${chatHistory
            .map(
              (m) => `
                <div class="msg">
                  <div class="${m.sender}">${m.sender === "user" ? "User:" : "CoFi:"}</div>
                  <div class="bubble">${m.text}</div>
                </div>
              `
            )
            .join("")}
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.close(); }, 2000);
  };

  // Wizard Navigation Functions
  const handleNext = () => {
    setActiveStep((prevStep) =>
      Math.min(prevStep + 1, WIZARD_STEPS.length - 1)
    );
  };

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Step1SetupProfile
            wizardData={wizardData}
            setWizardData={setWizardData}
          />
        );
      case 1:
        return (
          <Step2LoadDatasets
            wizardData={wizardData}
            setWizardData={setWizardData}
          />
        );
      case 2:
        return (
          <Step3SetPreferences
            wizardData={wizardData}
            setWizardData={setWizardData}
          />
        );
      case 3:
        return (
          <Step4RunCoFi
            wizardData={wizardData}
            setWizardData={setWizardData}
            getAnswer={getAnswer}
            decodeHtml={decodeHtml}
          />
        );
      case 4:
        return (
          <Step5ReviewResults
            wizardData={wizardData}
            setWizardData={setWizardData}
            getAnswer={getAnswer}
            decodeHtml={decodeHtml}
          />
        );
      case 5:
        return (
          <Step6Deploy
            wizardData={wizardData}
            setWizardData={setWizardData}
          />
        );
      default:
        return <div>Unknown Step</div>;
    }
  };

  return (
    <Box sx={{ background: "#fcfcfc", display: "flex", width: "100%" }}>
      <Box
        className="page-container crm-container"
        sx={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <Box className="header-container">
          <Box className="page-title">CoFi Wizard</Box>
        </Box>

        {/* <Box sx={{ width: "80%", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="ChatBot" />
            <Tab label="CoFi Wizard" />
          </Tabs>
        </Box> */}

        {/* Tab 0: ChatBot */}
        {/* {activeTab === 0 && (
          <Box sx={{ width: "60%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
                mb: 1,
              }}
            >
              <IconButton onClick={() => handlePrintChat()} title="Print chat">
                <PrintIcon />
              </IconButton>
            </Box>

            <Slide direction="up" in={true} mountOnEnter>
              <Fade in={true}>
                <Box
                  ref={hasStartedChat ? chatContainerRef : null}
                  sx={{
                    flex: 1,
                    overflowY: hasStartedChat ? "auto" : "visible",
                    display: "flex",
                    flexDirection: "column",
                    padding: 2,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    mb: 2,
                    height: hasStartedChat ? "60vh" : "auto",
                    backgroundColor: "#fff",
                    transition: "all 0.3s ease",
                  }}
                >
                  {
                    <ChatBubble sender="bot">
                      👋 Hi! I'm <strong>CoFi</strong>.<br />
                      Ask me a question and I'll help you explore financial
                      insights.
                    </ChatBubble>
                  }

                  {hasStartedChat &&
                    chatHistory.map((msg, index) => (
                      <ChatBubble key={index} sender={msg.sender}>
                        {msg.text}
                      </ChatBubble>
                    ))}

                  {hasStartedChat && isThinking && (
                    <ChatBubble sender="bot" thinking>
                      <TypingIndicator text={thinkingText} />
                    </ChatBubble>
                  )}

                  {hasStartedChat && isTyping && !isThinking && (
                    <ChatBubble sender="bot" thinking>
                      <TypingIndicator />
                    </ChatBubble>
                  )}
                </Box>
              </Fade>
            </Slide>

            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: "24px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <Autocomplete
                options={STATIC_QA}
                getOptionLabel={(option) => option.question}
                onChange={handleSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Ask CoFi something..."
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
                fullWidth
              />
            </Box>
          </Box>
        )} */}

        {/* Tab 1: CoFi Wizard */}
        {activeTab === 1 && (
          <Box
            sx={{
              width: "80%",
              mt: 2,
              height: "calc(90vh - 150px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {WIZARD_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                mb: 3,
                pr: 2,
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
              }}
            >
              {renderStepContent(activeStep)}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 3,
                pt: 2,
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#fcfcfc",
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                {activeStep === WIZARD_STEPS.length - 1 ? (
                  <Button onClick={handleReset} variant="contained">
                    Start Over
                  </Button>
                ) : (
                  <Button onClick={handleNext} variant="contained">
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Tab 2: Training Docs */}
        {activeTab === 2 && (
          <Box sx={{ width: "60%", mt: 2 }}>
            <CofiFileUploads />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Step 1: Set up CoFi Profile
const Step1SetupProfile = ({ wizardData, setWizardData }) => {
  const handleChange = (field, value) => {
    setWizardData((prev) => ({
      ...prev,
      profile: { ...prev.profile, [field]: value },
    }));
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Set up CoFi Profile
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Provide essential information to set up your profile.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Organization Name"
              value={wizardData.profile.organizationName}
              onChange={(e) => handleChange("organizationName", e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={wizardData.profile.industry}
                onChange={(e) => handleChange("industry", e.target.value)}
                label="Industry"
              >
                <MenuItem value="Finance">Finance</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Technology">Technology</MenuItem>
                <MenuItem value="Retail">Retail</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Target Demographic</InputLabel>
              <Select
                value={wizardData.profile.targetDemographic}
                onChange={(e) =>
                  handleChange("targetDemographic", e.target.value)
                }
                label="Target Demographic"
              >
                <MenuItem value="Gig Workers">Gig Workers</MenuItem>
                <MenuItem value="Students">Students</MenuItem>
                <MenuItem value="Indigenous Communities">
                  Indigenous Communities
                </MenuItem>
                <MenuItem value="Home Owners">Home Owners</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Demographic Sample Size"
              value={wizardData.profile.sampleSize}
              onChange={(e) => handleChange("sampleSize", e.target.value)}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Step 2: Load Datasets
const Step2LoadDatasets = ({ wizardData, setWizardData }) => {
  return (
    <Card>
      <CardContent sx={{ maxHeight: "500px", overflowY: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Load Datasets
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Upload or link collaboration datasets. All data will be securely
          stored in S3 bucket.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <CofiFileUploads />
        </Box>

        <Box
          sx={{ mt: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
        >
          <Typography variant="body2" color="textSecondary" gutterBottom>
            📄 Supported formats: CSV, Excel, JSON
          </Typography>
          <Typography variant="body2" color="textSecondary">
            📦 Maximum file size: 50MB
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Step 3: Set Preferences
const Step3SetPreferences = ({ wizardData, setWizardData }) => {
  const handleChange = (field, value) => {
    setWizardData((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, [field]: value },
    }));
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Set Preferences
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Configure AI decision rules and financial model guidelines.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>LLM Model</InputLabel>
              <Select
                value={wizardData.preferences.llmModel}
                onChange={(e) => handleChange("llmModel", e.target.value)}
                label="LLM Model"
              >
                <MenuItem value="Amazon Bedrock">Amazon Bedrock</MenuItem>
                <MenuItem value="OpenAI GPT-4">OpenAI GPT-4</MenuItem>
                <MenuItem value="Claude">Claude</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>CoFi Partner</InputLabel>
              <Select
                value={wizardData.preferences.cofiPartner}
                onChange={(e) => handleChange("cofiPartner", e.target.value)}
                label="CoFi Partner"
              >
                <MenuItem value="Telecom">Telecom</MenuItem>
                <MenuItem value="Banking">Banking</MenuItem>
                <MenuItem value="Insurance">Insurance</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Risk Tolerance</InputLabel>
              <Select
                value={wizardData.preferences.riskTolerance}
                onChange={(e) => handleChange("riskTolerance", e.target.value)}
                label="Risk Tolerance"
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Step 4: Run CoFi
const Step4RunCoFi = ({ wizardData, setWizardData, getAnswer, decodeHtml }) => {
  const [isRunning, setIsRunning]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [isFetching, setIsFetching]   = useState(false);
  const [fetchStep, setFetchStep]     = useState(0);
  const [fetchDone, setFetchDone]     = useState(false);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [localChatHistory, setLocalChatHistory] = useState([]);
  const chatContainerRef = React.useRef(null);
  const fetchStepRef = useRef(null);

  const FETCH_STEPS = [
    "Connecting to AI model...",
    "Analyzing financial data...",
    "Generating insights...",
    "Processing recommendations...",
    "Finalizing results...",
  ];

  useEffect(() => {
    if (progress === 100) {
      fetchAnswers();
    }
  }, [progress]);

  // Cycle through animated fetch step labels while fetching
  useEffect(() => {
    if (!isFetching) {
      if (fetchStepRef.current) { clearInterval(fetchStepRef.current); fetchStepRef.current = null; }
      return;
    }
    setFetchStep(0);
    fetchStepRef.current = setInterval(() => {
      setFetchStep(prev => (prev + 1) % FETCH_STEPS.length);
    }, 1800);
    return () => { if (fetchStepRef.current) clearInterval(fetchStepRef.current); };
  }, [isFetching]);

  const fetchAnswers = async () => {
    setIsFetching(true);
    setFetchDone(false);
    for (const question of pendingQuestions) {
      const response = await getAnswer(question);
      const answer = decodeHtml(response);
      setWizardData((prev) => ({
        ...prev,
        results: [...prev.results, { question, answer }],
      }));
    }
    setPendingQuestions([]);
    setIsFetching(false);
    setIsRunning(false);
    setFetchDone(true);
  };

  const handleRunModel = () => {
    setIsRunning(true);
    setFetchDone(false);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 10;
        if (next >= 100) {
          clearInterval(interval);
          return 100;
        }
        return next;
      });
    }, 500);
  };

  const handleSelect = (_, value) => {
    if (!value) return;
    const question = value.question;
    setPendingQuestions((prev) => [...prev, question]);
    setLocalChatHistory((prev) => [
      ...prev,
      { sender: "user", text: question },
    ]);
    setTimeout(scrollToBottom, 0);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const handlePrintChat = () => {
    const printWindow = window.open("", "_blank");
    const html = `
      <html>
        <head>
          <title>CoFi Chat</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h2 { margin-bottom: 20px; }
            .msg { margin-bottom: 12px; }
            .user { font-weight: bold; }
            .bot { font-weight: bold; }
            .bubble { padding: 8px 12px; border-radius: 8px; background: #f3f3f3; display: inline-block; max-width: 90%; }
          </style>
        </head>
        <body>
          <h2>CoFi Conversation</h2>
          ${localChatHistory
            .map((m) => `
                <div class="msg">
                  <div class="${m.sender}">${m.sender === "user" ? "User:" : "CoFi:"}</div>
                  <div class="bubble">${m.text}</div>
                </div>
              `).join("")}
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.close(); }, 2000);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>Run CoFi</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Initiate the CoFi engine to generate financial outcomes.
        </Typography>

        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" gutterBottom>
            Profile: {wizardData.profile.organizationName || "Not Set"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Industry: {wizardData.profile.industry || "Not Set"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            LLM Model: {wizardData.preferences.llmModel}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleRunModel}
            disabled={isRunning || isFetching}
            sx={{ mt: 3 }}
          >
            {isRunning && progress < 100
              ? `Running... ${progress}%`
              : isFetching
              ? "Processing..."
              : fetchDone
              ? "✓ Run Complete — Run Again"
              : "Run Model"}
          </Button>

          {/* Phase 1: progress bar 0 → 100% */}
          {isRunning && progress < 100 && (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Box sx={{ height: 8, backgroundColor: "#e0e0e0", borderRadius: 4, overflow: "hidden" }}>
                <Box sx={{
                  height: "100%", backgroundColor: "#1976d2",
                  width: `${progress}%`, transition: "width 0.3s",
                }} />
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
                Initializing model pipeline...
              </Typography>
            </Box>
          )}

          {/* Phase 2: AI fetch loading overlay */}
          {isFetching && (
            <Box sx={{
              mt: 3, p: 3,
              border: "1px solid #e3f2fd",
              borderRadius: 3,
              backgroundColor: "#f8fdff",
              textAlign: "center",
            }}>
              {/* Animated pulse ring */}
              <Box sx={{
                width: 64, height: 64,
                borderRadius: "50%",
                border: "3px solid #e0e0e0",
                borderTop: "3px solid #2a7886",
                margin: "0 auto 16px",
                animation: "cofi-spin 1s linear infinite",
                "@keyframes cofi-spin": {
                  "0%":   { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2a7886", mb: 0.5 }}>
                CoFi AI is working...
              </Typography>

              {/* Cycling step label */}
              <Typography variant="body2" color="textSecondary" sx={{
                minHeight: 20,
                transition: "opacity 0.4s ease",
              }}>
                {FETCH_STEPS[fetchStep]}
              </Typography>

              {/* Indeterminate shimmer bar */}
              <Box sx={{
                mt: 2, height: 6, borderRadius: 3,
                backgroundColor: "#e0e0e0", overflow: "hidden",
                position: "relative",
              }}>
                <Box sx={{
                  position: "absolute", top: 0, left: 0,
                  height: "100%", width: "40%",
                  backgroundColor: "#2a7886",
                  borderRadius: 3,
                  animation: "cofi-shimmer 1.6s ease-in-out infinite",
                  "@keyframes cofi-shimmer": {
                    "0%":   { left: "-40%" },
                    "100%": { left: "100%" },
                  },
                }} />
              </Box>

              {/* Per-question fetch dots */}
              {pendingQuestions.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                  {pendingQuestions.map((_, i) => (
                    <Box key={i} sx={{
                      width: 8, height: 8, borderRadius: "50%",
                      backgroundColor: "#2a7886",
                      animation: "cofi-bounce 1.2s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                      "@keyframes cofi-bounce": {
                        "0%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
                        "50%":      { opacity: 1,   transform: "scale(1.3)" },
                      },
                    }} />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Phase 3: done confirmation */}
          {fetchDone && !isFetching && (
            <Box sx={{
              mt: 3, p: 2,
              backgroundColor: "#f0fdf4", borderRadius: 2,
              border: "1px solid #bbf7d0",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                Results generated — proceed to Review Results
              </Typography>
            </Box>
          )}
        </Box>

        {/* <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Ask CoFi a Question</Typography>
          <Box sx={{
            border: "1px solid #ddd", borderRadius: "24px", padding: "10px 12px",
            display: "flex", alignItems: "center",
            backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", mb: 2,
          }}>
            <Autocomplete
              options={STATIC_QA}
              getOptionLabel={(option) => option.question}
              onChange={handleSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a question..."
                  variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                />
              )}
              fullWidth
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%", mb: 1 }}>
            <IconButton onClick={handlePrintChat} title="Print chat">
              <PrintIcon />
            </IconButton>
          </Box>

          <Slide direction="up" in={true} mountOnEnter>
            <Fade in={true}>
              <Box ref={chatContainerRef} sx={{
                overflowY: "auto", display: "flex", flexDirection: "column",
                padding: 2, border: "1px solid #ddd", borderRadius: 2,
                backgroundColor: "#fff", height: "300px",
              }}>
                {localChatHistory.map((msg, index) => (
                  <ChatBubble key={index} sender={msg.sender}>{msg.text}</ChatBubble>
                ))}
              </Box>
            </Fade>
          </Slide>
        </Box> */}
      </CardContent>
    </Card>
  );
};

// ─── CoFilab-style sliding panel shell ───────────────────────────────────────
const SidePanel = ({ open, onClose, children }) => (
  <>
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 1200, opacity: open ? 1 : 0,
      pointerEvents: open ? "all" : "none",
      transition: "opacity 0.3s ease",
    }} />
    <div style={{
      position: "fixed", top: 0, right: 0, width: "680px", maxWidth: "100vw",
      height: "100vh", backgroundColor: "#fff",
      boxShadow: "-6px 0 32px rgba(0,0,0,0.18)",
      zIndex: 1300, display: "flex", flexDirection: "column",
      transform: open ? "translateX(0)" : "translateX(100%)",
      transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
    }}>
      {/* Close button — top-left teal, exactly like CoFilab screenshot */}
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #e8e8e8", flexShrink: 0 }}>
        <button onClick={onClose} style={{
          backgroundColor: "#2a7886", color: "#fff", border: "none",
          borderRadius: "6px", padding: "8px 20px", fontSize: "13px",
          fontWeight: 700, cursor: "pointer",
        }}>Close</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "30px 36px" }}>
        {children}
      </div>
    </div>
  </>
);

// ─── Shared panel typography helpers ─────────────────────────────────────────
const PanelH1    = ({ children }) => <h2 style={{ fontSize: "24px", fontWeight: 400, marginBottom: "6px", marginTop: 0 }}>{children}</h2>;
const PanelH2    = ({ children }) => <h3 style={{ fontSize: "20px", fontWeight: 400, marginBottom: "10px", marginTop: "28px" }}>{children}</h3>;
const PanelH3    = ({ children }) => <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px", marginTop: "20px" }}>{children}</h4>;
const PanelText  = ({ children }) => <p style={{ fontSize: "14px", color: "#333", lineHeight: 1.7, margin: "0 0 12px" }}>{children}</p>;
const PanelDivider = () => <hr style={{ border: "none", borderTop: "1px solid #e8e8e8", margin: "20px 0" }} />;

const PanelInfoRow = ({ label, value }) => (
  <div style={{ marginBottom: "4px", fontSize: "14px" }}>
    <strong>{label}:</strong> {value}
  </div>
);

const TealButton = ({ children, onClick }) => (
  <button onClick={onClick} style={{
    backgroundColor: "#2a7886", color: "#fff", border: "none",
    borderRadius: "6px", padding: "10px 20px", fontSize: "13px",
    fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
  }}>{children}</button>
);

const ActionButtonRow = ({ onAccept, onReject, onRegenerate, onUpdateDecisions, onShareReport }) => (
  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "16px 0" }}>
    <TealButton onClick={onAccept}>Accept</TealButton>
    <TealButton onClick={onReject}>Reject</TealButton>
    <TealButton onClick={onRegenerate}>Re-Generate</TealButton>
    <TealButton onClick={onUpdateDecisions}>Update Decisions</TealButton>
    <TealButton onClick={onShareReport}>Share Report</TealButton>
  </div>
);

// ─── Chart.js bar chart — matches screenshot exactly ─────────────────────────
const BarChart = ({ data }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          label: "Gig Worker Activity by Season",
          data: data.map(d => d.value),
          backgroundColor: data.map(d => d.color),
          borderRadius: 2,
        }],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { position: "top", align: "start" },
        },
        scales: {
          y: { beginAtZero: true, max: 80, ticks: { stepSize: 10 } },
        },
      },
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, []);

  return <canvas ref={canvasRef} style={{ maxHeight: 280 }} />;
};

// ─── Chart.js pie chart — matches screenshot exactly ─────────────────────────
const PieChart = ({ data }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

    chartRef.current = new Chart(canvasRef.current, {
      type: "pie",
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          data: data.map(d => d.value),
          backgroundColor: data.map(d => d.color),
        }],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { position: "bottom" },
        },
      },
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, []);

  return <canvas ref={canvasRef} style={{ maxHeight: 320 }} />;
};

// ─── ROI Report panel — matches screenshots exactly ───────────────────────────
const ROIReportPanel = ({ result }) => (
  <div>
    <PanelH1>CO-FI ROI Report: {result.productName}</PanelH1>
    <PanelDivider />
    <PanelH2>Loan Program / Service Details</PanelH2>
    <ActionButtonRow />
    <PanelInfoRow label="Interest Rate" value={result.interestRate} />
    <PanelInfoRow label="Loan Term" value={result.loanTerm} />
    <PanelInfoRow label="Target Audience" value={result.targetAudience} />
    <PanelInfoRow label="Capital Requirement" value="$100,000" />
    <PanelInfoRow label="Repayment Cycle" value="24/48 hours (short-term loans tailored to gig workers)" />

    <PanelH2>Introduction</PanelH2>
    <PanelText>
      The {result.productName} is designed to support gig workers who are part of the growing freelance and
      contract-based workforce in Canada. These individuals often face financial barriers due to irregular incomes,
      making it difficult to access traditional financing options. The goal of this loan product is to provide
      affordable, flexible financing that aligns with the cash flow cycles of gig workers.
    </PanelText>

    <PanelH2>Partner Suggestion</PanelH2>
    <PanelText>
      To reach gig workers effectively, partnerships with banking companies and insurance companies would be ideal.
      Banking companies can help identify gig workers who rely heavily on mobile data usage for their jobs (e.g.,
      ride-sharing, food delivery services). Insurance companies can provide bundled offers that include loan
      protection plans tailored to freelance professionals.
    </PanelText>

    <PanelH3>Banking Partnership Suggestions:</PanelH3>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
      <div style={{ flex: 1 }}>
        <PanelH3>1. The Central Bank of the Republic of Türkiye</PanelH3>
        <PanelText>
          The Turkish banking sector is a robust, well-regulated, and technologically advanced system, featuring a mix of major state-owned and private banks like Ziraat, İş Bankası, and Garanti BBVA
        </PanelText>
        {/* <PanelText><strong>Key Benefit:</strong> By partnering with Rogers, the loan program could offer discounted data plans bundled with loan offers, making it an attractive deal for gig workers.</PanelText> */}
      </div>
      <TealButton>Initiate Partner</TealButton>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
      <div style={{ flex: 1 }}>
        <PanelH3>2. Ziraat Bank</PanelH3>
        <PanelText>
          Ziraat Bankası is a Turkish public bank founded in 1863. The bank provides commercial loan support to companies and tradesmen, as well as personal loans such as consumer loans, vehicle loans and housing loans.
        </PanelText>
        {/* <PanelText><strong>Key Benefit:</strong> A partnership with Bell could offer data perks for loan recipients who depend on mobile devices for gig work.</PanelText> */}
      </div>
      <TealButton>Initiate Partner</TealButton>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
      <div style={{ flex: 1 }}>
        <PanelH3>3. Garanti BBVA</PanelH3>
        <PanelText>
          Garanti BBVA is a Turkish financial services company based in Turkey. 86% of Garanti's stakes are owned by the Spanish bank Banco Bilbao Vizcaya Argentaria.
        </PanelText>
        {/* <PanelText><strong>Key Benefit:</strong> A Telus partnership could target gig workers who need consistent mobile access in remote or suburban areas.</PanelText> */}
      </div>
      <TealButton>Initiate Partner</TealButton>
    </div>

    <PanelH2>Market Location</PanelH2>
    <PanelText>
      The initial market focus is on urban areas in Ontario, particularly Toronto, Ottawa, and Hamilton. These
      cities have a high concentration of gig workers involved in ride-sharing services, delivery platforms, and
      freelance jobs across various industries.
    </PanelText>

    <PanelH2>Target Gig Workers Profile</PanelH2>
    <PanelInfoRow label="Age Range" value="24-45 years old" />
    <PanelInfoRow label="Annual Salary" value="$30,000 - $50,000" />
    <PanelInfoRow label="Mobile Data Usage" value="5GB - 10GB per month (for job-related activities)" />
    <PanelInfoRow label="Auto Ownership" value="70% of gig workers own or lease a car" />
    <PanelInfoRow label="Primary Gig Industries" value="Ride-sharing (Uber, Lyft), Food Delivery (DoorDash, UberEats), Freelancing (Upwork, Fiverr)" />

    <PanelH2>Seasonality of Gig Workers</PanelH2>
    <BarChart data={[
      { label: "Winter", value: 30, color: "#4caf50" },
      { label: "Spring", value: 40, color: "#ff9800" },
      { label: "Summer", value: 70, color: "#03a9f4" },
      { label: "Fall",   value: 60, color: "#f44336" },
    ]} />

    <PanelH2>Gig Worker Distribution</PanelH2>
    <PieChart data={[
      { label: "Ride-sharing",  value: 40, color: "#ff6384" },
      { label: "Food Delivery", value: 30, color: "#36a2eb" },
      { label: "Freelancing",   value: 20, color: "#cc65fe" },
      { label: "Other",         value: 10, color: "#ffce56" },
    ]} />

    <PanelH2>Loan Write-Up</PanelH2>
    <PanelText>
      The {result.productName} requires a capital outlay of $100,000, allocated to provide 24/48-hour short-term
      loans ranging from $500 to $5,000. The repayment terms are aligned with gig workers' earning cycles,
      allowing them to repay in small increments or lump sums depending on their cash flow. The flexibility of
      this loan makes it particularly attractive to gig workers who face irregular income schedules.
    </PanelText>

    <PanelH2>Capital Requirements and Repayments</PanelH2>
    <PanelText>
      With an interest rate of {result.interestRate} and a repayment term of {result.loanTerm}, gig workers can
      repay their loans in daily or weekly installments based on their income frequency, ensuring they have the
      flexibility to meet their financial commitments without disrupting their cash flow.
    </PanelText>
  </div>
);

// ─── Deploy Activity panel ────────────────────────────────────────────────────
const DeployPanel = ({ result }) => {
  const [deploying, setDeploying]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [stepIdx, setStepIdx]       = useState(0);
  const [deployed, setDeployed]     = useState(false);

  const steps = [
    "Validating model configuration",
    "Running compliance checks",
    "Provisioning infrastructure",
    "Deploying financial product",
    "Activating monitoring",
  ];

  const handleDeploy = () => {
    setDeploying(true); setProgress(0); setStepIdx(0);
    const iv = setInterval(() => {
      setStepIdx(prev => {
        const next = prev + 1;
        setProgress(Math.round((next / steps.length) * 100));
        if (next >= steps.length) {
          clearInterval(iv);
          setTimeout(() => { setDeployed(true); setDeploying(false); }, 500);
        }
        return next < steps.length ? next : prev;
      });
    }, 800);
  };

  return (
    <div>
      <PanelH1>Deploy: {result.productName}</PanelH1>
      <PanelDivider />

      {!deployed ? (
        <>
          <PanelH2>Deployment Summary</PanelH2>
          <PanelInfoRow label="Product"         value={result.productName} />
          <PanelInfoRow label="Interest Rate"   value={result.interestRate} />
          <PanelInfoRow label="Loan Term"       value={result.loanTerm} />
          <PanelInfoRow label="Target Audience" value={result.targetAudience} />
          <PanelInfoRow label="LLM Model"       value="Amazon Bedrock" />
          <PanelInfoRow label="Environment"     value="Production" />

          {deploying && (
            <>
              <PanelH2>Deployment Progress — {progress}%</PanelH2>
              <div style={{ height: 8, backgroundColor: "#e9ecef", borderRadius: 4, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ height: "100%", width: `${progress}%`, backgroundColor: "#2a7886", transition: "width 0.4s ease" }} />
              </div>
              {steps.map((s, i) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f5f5f5", color: i < stepIdx ? "#4caf50" : i === stepIdx ? "#2a7886" : "#bbb" }}>
                  <span>{i < stepIdx ? "✓" : i === stepIdx ? "⟳" : "○"}</span>
                  <span style={{ fontSize: 13, fontWeight: i <= stepIdx ? 600 : 400 }}>{s}</span>
                </div>
              ))}
            </>
          )}

          <div style={{ marginTop: 24 }}>
            <TealButton onClick={handleDeploy} disabled={deploying}>
              {deploying ? `Deploying... ${progress}%` : "🚀  Deploy Now"}
            </TealButton>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <PanelH1>Successfully Deployed!</PanelH1>
          <PanelText><strong>{result.productName}</strong> is now live and available to your target audience.</PanelText>
          <div style={{ marginTop: 20, padding: 16, backgroundColor: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", fontSize: 13, color: "#555" }}>
            Monitor your deployment from the <strong>CoFi Dashboard</strong>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Rejected panel ───────────────────────────────────────────────────────────
const RejectedPanel = ({ result }) => (
  <div>
    <PanelH1>Rejection Analysis: {result.productName}</PanelH1>
    <PanelDivider />

    <div style={{ padding: "12px 16px", backgroundColor: "#fff5f5", borderRadius: 8, border: "1px solid #ffcdd2", marginBottom: 24, fontSize: 13, color: "#555", lineHeight: 1.6 }}>
      ⚠️ This product did not meet the required thresholds for approval. Review the reasons below and adjust the model configuration before resubmitting.
    </div>

    <PanelH2>Rejection Reasons</PanelH2>
    {[
      { cat: "Risk Assessment",        detail: "Debt-to-income ratio exceeds acceptable threshold for this demographic segment.",                                       sev: "High",   bg: "#fff5f5", border: "#ffcdd2", chip: "#f44336" },
      { cat: "Regulatory Compliance",  detail: "Product structure requires additional review under current consumer protection guidelines.",                             sev: "Medium", bg: "#fff8f0", border: "#ffe0b2", chip: "#ff9800" },
      { cat: "Market Viability",       detail: "Insufficient demand signals detected in the target market data.",                                                       sev: "Low",    bg: "#fffde7", border: "#fff9c4", chip: "#ffc107" },
    ].map(r => (
      <div key={r.cat} style={{ marginBottom: 12, padding: 14, borderRadius: 8, border: `1px solid ${r.border}`, backgroundColor: r.bg }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <strong style={{ fontSize: 13 }}>{r.cat}</strong>
          <span style={{ padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, backgroundColor: r.chip, color: "#fff" }}>{r.sev}</span>
        </div>
        <PanelText>{r.detail}</PanelText>
      </div>
    ))}

    <PanelH2>Recommendations to Resubmit</PanelH2>
    {[
      "Reduce the loan term from 60 to 36 months to lower risk exposure",
      "Adjust interest rate to better reflect current market conditions",
      "Expand target demographic to include home equity owners",
      "Incorporate additional collateral requirements",
    ].map((rec, i) => (
      <div key={i} style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-start" }}>
        <div style={{ minWidth: 24, height: 24, borderRadius: "50%", backgroundColor: "#2a7886", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
        <PanelText>{rec}</PanelText>
      </div>
    ))}

    <PanelDivider />
    <TealButton>Resubmit with Adjustments</TealButton>
  </div>
);

// ─── Under Review panel ───────────────────────────────────────────────────────
const ReviewPanel = ({ result }) => (
  <div>
    <PanelH1>Under Review: {result.productName}</PanelH1>
    <PanelDivider />
    <div style={{ padding: "12px 16px", backgroundColor: "#fff8f0", borderRadius: 8, border: "1px solid #ffe0b2", marginBottom: 24, fontSize: 13, color: "#555", lineHeight: 1.6 }}>
      🔍 This product is currently under compliance and risk review. It will be available for deployment once all checks pass.
    </div>
    <PanelH2>Review Checklist</PanelH2>
    {[
      { label: "Credit Risk Assessment",       done: true  },
      { label: "Regulatory Compliance Check",  done: true  },
      { label: "Market Viability Analysis",    done: false },
      { label: "Legal Review",                 done: false },
      { label: "Final Approval",               done: false },
    ].map(item => (
      <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
        <span style={{ fontSize: 16, color: item.done ? "#4caf50" : "#ccc" }}>{item.done ? "✓" : "○"}</span>
        <span style={{ fontSize: 13, fontWeight: item.done ? 700 : 400, color: item.done ? "#4caf50" : "#666", flex: 1 }}>{item.label}</span>
        {item.done && <span style={{ fontSize: 11, color: "#4caf50", fontWeight: 600 }}>Complete</span>}
      </div>
    ))}
    <PanelH2>Product Details</PanelH2>
    <PanelInfoRow label="Product"              value={result.productName} />
    <PanelInfoRow label="Interest Rate"        value={result.interestRate} />
    <PanelInfoRow label="Loan Term"            value={result.loanTerm} />
    <PanelInfoRow label="Target Audience"      value={result.targetAudience} />
    <PanelInfoRow label="Estimated Completion" value="3–5 business days" />
  </div>
);

// ─── Step 5: Review Results ───────────────────────────────────────────────────
const Step5ReviewResults = ({ wizardData, setWizardData, getAnswer, decodeHtml }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelType, setPanelType] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  // For chat bot
  const [chatHistory, setChatHistory] = useState([]);
  const chatContainerRef = React.useRef(null);
  const hasStartedChat = chatHistory.length > 0;
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState("Thinking");

  const THINKING_MESSAGES = [
    "Thinking",
    "Analyzing your question",
    "Reviewing insights",
    "Connecting the dots",
    "Almost there",
  ];

  const mockResults = [
    { id: 1, productName: "Gig Economy Booster Loan", interestRate: "3.5%", loanTerm: "1-2 months", targetAudience: "Gig Workers", status: "Recommended" },
    { id: 2, productName: "Student Support Loan", interestRate: "3.9%", loanTerm: "24 months", targetAudience: "Students", status: "Under Review" },
    { id: 3, productName: "Homeowner Renovation Loan", interestRate: "4.2%", loanTerm: "60 months", targetAudience: "Home Owners", status: "Rejected" },
  ];

  const openPanel = (type, result) => {
    setSelectedResult(result);
    setPanelType(type);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setTimeout(() => { setPanelType(null); setSelectedResult(null); }, 350);
  };

  // panelTitle not needed — PhpPanel handles its own header via the Close button

  const statusBadgeStyle = (status) => ({
    padding: "4px 12px", borderRadius: "4px", fontSize: "12px", color: "#fff",
    backgroundColor: status === "Recommended" ? "#4caf50" : status === "Under Review" ? "#ff9800" : "#f44336",
  });

  const renderActionButton = (result) => {
    const base = {
      border: "none", borderRadius: "6px", padding: "6px 14px",
      fontSize: "12px", fontWeight: 600, cursor: "pointer", color: "#fff",
      display: "flex", alignItems: "center", gap: "5px",
    };
    if (result.status === "Recommended") return (
      <button style={{ ...base, backgroundColor: "#4caf50" }} onClick={() => openPanel("deploy", result)}>
        Deploy
      </button>
    );
    if (result.status === "Under Review") return (
      <button style={{ ...base, backgroundColor: "#ff9800" }} onClick={() => openPanel("review", result)}>
        View Review
      </button>
    );
    if (result.status === "Rejected") return (
      <button style={{ ...base, backgroundColor: "#f44336" }} onClick={() => openPanel("rejected", result)}>
        ✕ Rejected
      </button>
    );
    return null;
  };

  const renderPanelContent = () => {
    if (!selectedResult) return null;
    if (panelType === "roi")      return <ROIReportPanel result={selectedResult} />;
    if (panelType === "deploy")   return <DeployPanel    result={selectedResult} />;
    if (panelType === "rejected") return <RejectedPanel  result={selectedResult} />;
    if (panelType === "review")   return <ReviewPanel    result={selectedResult} />;
    return null;
  };

  // Functions required for the chatbot
  useEffect(() => {
    if (!isThinking) return;

    let index = 0;

    const interval = setInterval(() => {
      index = (index + 1) % THINKING_MESSAGES.length;
      setThinkingText(THINKING_MESSAGES[index]);
    }, 5000);

    return () => clearInterval(interval);
  }, [isThinking]);

  useEffect(() => {
    if (!selectedQuestion) return;

    const fetchAnswer = async () => {
      const response = await getAnswer(selectedQuestion);
      const answer = decodeHtml(response);

      setIsThinking(false);
      setIsTyping(true);

      let index = 0;

      const interval = setInterval(() => {
        index++;

        setChatHistory((prev) => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;

          updated[lastIndex] = {
            ...updated[lastIndex],
            text: answer.slice(0, index),
          };

          return updated;
        });

        scrollToBottom();

        if (index >= answer.length) {
          clearInterval(interval);
          setIsTyping(false);
        }
      }, 20);
    };

    fetchAnswer();
  }, [selectedQuestion]);

  const handleSelect = (_, value) => {
    if (!value) return;

    if (!hasWelcomed) {
      setHasWelcomed(true);
    }
    setSelectedQuestion(value.question);

    setIsThinking(true);
    setIsTyping(true);

    setChatHistory((prev) => [
      ...prev,
      { sender: "user", text: value.question },
      { sender: "bot", text: "" },
    ]);

    setTimeout(scrollToBottom, 0);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const handlePrintChat = () => {
    const printWindow = window.open("", "_blank");

    const html = `
      <html>
        <head>
          <title>CoFi Chat</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h2 { margin-bottom: 20px; }
            .msg { margin-bottom: 12px; }
            .user { font-weight: bold; }
            .bot { font-weight: bold; }
            .bubble { padding: 8px 12px; border-radius: 8px; background: #f3f3f3; display: inline-block; max-width: 90%; }
          </style>
        </head>
        <body>
          <h2>CoFi Conversation</h2>
          ${chatHistory
            .map(
              (m) => `
                <div class="msg">
                  <div class="${m.sender}">${m.sender === "user" ? "User:" : "CoFi:"}</div>
                  <div class="bubble">${m.text}</div>
                </div>
              `
            )
            .join("")}
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.close(); }, 2000);
  };

  const TypingIndicator = ({ text }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        color: "#555",
        fontStyle: "italic",
      }}
    >
      {text && <span>{text}</span>}
      <Box sx={{ display: "flex", gap: "4px" }}>
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </Box>
    </Box>
  );

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>Review Results</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Analyze the results, including ROI and risks.
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  {["Product Name", "Interest Rate", "Loan Term", "Target Audience", "Status", "Report", "Action"].map((col) => (
                    <th key={col} style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #ddd", whiteSpace: "nowrap", fontSize: "13px" }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mockResults.map((result) => (
                  <tr key={result.id}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = "#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                  >
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd", fontSize: "13px" }}>{result.productName}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd", fontSize: "13px" }}>{result.interestRate}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd", fontSize: "13px" }}>{result.loanTerm}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd", fontSize: "13px" }}>{result.targetAudience}</td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd" }}>
                      <span style={statusBadgeStyle(result.status)}>{result.status}</span>
                    </td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd" }}>
                      <button onClick={() => openPanel("roi", result)} style={{
                        border: "1px solid #2a7886", borderRadius: "6px", padding: "6px 12px",
                        fontSize: "12px", fontWeight: 600, cursor: "pointer",
                        backgroundColor: "transparent", color: "#2a7886", whiteSpace: "nowrap",
                      }}>
                         ROI Report
                      </button>
                    </td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #ddd" }}>
                      {renderActionButton(result)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <Typography variant="h6" sx={{ mt: 4 }}>Generated Insights</Typography>
          <Grid container spacing={2}>
            {wizardData.results.map((res, index) => (
              <Grid item xs={12} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Question: {res.question}</Typography>
                    <Typography variant="body2">{res.answer}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Chatbot */}
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
                mb: 1,
              }}
            >
              <IconButton onClick={() => handlePrintChat()} title="Print chat">
                <PrintIcon />
              </IconButton>
            </Box>

            <Slide direction="up" in={true} mountOnEnter>
              <Fade in={true}>
                <Box
                  ref={hasStartedChat ? chatContainerRef : null}
                  sx={{
                    flex: 1,
                    overflowY: hasStartedChat ? "auto" : "visible",
                    display: "flex",
                    flexDirection: "column",
                    padding: 2,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    mb: 2,
                    height: hasStartedChat ? "60vh" : "auto",
                    backgroundColor: "#fff",
                    transition: "all 0.3s ease",
                  }}
                >
                  {
                    <ChatBubble sender="bot">
                      👋 Hi! I'm <strong>CoFi</strong>.<br />
                      Ask me a question and I'll help you explore financial
                      insights.
                    </ChatBubble>
                  }

                  {hasStartedChat &&
                    chatHistory.map((msg, index) => (
                      <ChatBubble key={index} sender={msg.sender}>
                        {msg.text}
                      </ChatBubble>
                    ))}

                  {hasStartedChat && isThinking && (
                    <ChatBubble sender="bot" thinking>
                      <TypingIndicator text={thinkingText} />
                    </ChatBubble>
                  )}

                  {hasStartedChat && isTyping && !isThinking && (
                    <ChatBubble sender="bot" thinking>
                      <TypingIndicator />
                    </ChatBubble>
                  )}
                </Box>
              </Fade>
            </Slide>

            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: "24px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                marginBottom: '20px',
              }}
            >
              <Autocomplete
                options={STATIC_QA}
                getOptionLabel={(option) => option.question}
                onChange={handleSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Ask CoFi something..."
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
                fullWidth
              />
            </Box>
          </Box>
          
        </CardContent>
      </Card>

      <SidePanel open={panelOpen} onClose={closePanel}>
        {renderPanelContent()}
      </SidePanel>
    </>
  );
};

// Step 6: Deploy & Launch
const Step6Deploy = ({ wizardData, setWizardData }) => {
  const [deployed, setDeployed] = useState(false);

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Deploy & Launch
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Deploy the final CoFi model and launch your solution.
        </Typography>

        <Box sx={{ textAlign: "center", py: 4 }}>
          {!deployed ? (
            <>
              <Typography variant="h6" gutterBottom>
                Ready to Deploy Your CoFi Model
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 3 }}
              >
                Once deployed, your financial products will be available to your
                target audience.
              </Typography>
              <Button
                variant="contained"
                size="large"
                color="success"
                onClick={() => setDeployed(true)}
              >
                Deploy Now
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h4" gutterBottom color="success.main">
                ✓ Successfully Deployed!
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                Your CoFi model is now live and ready to serve your customers.
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1 }}
              >
                You can monitor the deployment from the CoFi Dashboard.
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const TypingIndicator = ({ text }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 14px",
      color: "#555",
      fontStyle: "italic",
    }}
  >
    {text && <span>{text}</span>}
    <Box sx={{ display: "flex", gap: "4px" }}>
      <span className="dot" />
      <span className="dot" />
      <span className="dot" />
    </Box>
  </Box>
);

export default CoFi;